import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { isPlatformOtpRequiredResponse } from "@/services/authService";

interface AdminLoginFormValues {
  email: string;
  password: string;
}

export function AdminLoginPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") ?? "/";
  const { loginWithPassword, submitPlatformLoginOtp, resendPlatformLoginOtp } =
    useAuth();

  const [otpStep, setOtpStep] = useState<{
    pendingLoginId: string;
    email: string;
  } | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendBusy, setOtpResendBusy] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<AdminLoginFormValues>();

  const onSubmit = async (values: AdminLoginFormValues): Promise<void> => {
    try {
      const locale = i18n.resolvedLanguage ?? i18n.language;
      const result = await loginWithPassword(
        values.email,
        values.password,
        locale,
        true, // adminLogin flag
      );
      if (isPlatformOtpRequiredResponse(result)) {
        setOtpStep({ pendingLoginId: result.pendingLoginId, email: values.email });
        return;
      }
      // Shouldn't happen for platform users, but handle gracefully
      navigate(returnTo, { replace: true });
    } catch (err: unknown) {
      const message =
        err != null &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 403
          ? t("auth.adminLogin.noAccess")
          : t("auth.login.error", "Error al iniciar sesión.");
      toast.error(message);
    }
  };

  const handleOtpSubmit = async (): Promise<void> => {
    if (otpCode.length !== 6 || !otpStep) return;
    setOtpBusy(true);
    try {
      await submitPlatformLoginOtp(otpStep.pendingLoginId, otpCode);
      navigate(returnTo, { replace: true });
    } catch {
      toast.error(t("auth.login.platformOtp.invalidCode", "Código inválido. Intentá de nuevo."));
    } finally {
      setOtpBusy(false);
    }
  };

  const handleOtpResend = async (): Promise<void> => {
    if (!otpStep) return;
    setOtpResendBusy(true);
    try {
      await resendPlatformLoginOtp(otpStep.pendingLoginId);
      toast.success(t("auth.login.platformOtp.resendSuccess", "Código reenviado."));
    } catch {
      toast.error(t("auth.login.platformOtp.resendError", "No se pudo reenviar el código."));
    } finally {
      setOtpResendBusy(false);
    }
  };

  // OTP verification screen
  if (otpStep) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <div className="flex w-full max-w-md flex-col gap-6">
          <Card>
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center gap-2 text-center">
                  <img
                    src="/brand/gestionalo-logo.svg"
                    alt="Gestionalo"
                    className="mb-2 h-10 w-auto"
                  />
                  <h1 className="text-xl font-bold">
                    {t("auth.login.platformOtp.title")}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.login.platformOtp.instructions", { email: otpStep.email })}
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder={t("auth.login.platformOtp.codePlaceholder")}
                    className="text-center text-2xl tracking-[0.5em]"
                    value={otpCode}
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                  />
                  <Button
                    onClick={handleOtpSubmit}
                    disabled={otpCode.length !== 6 || otpBusy}
                    className="w-full"
                  >
                    {otpBusy && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    )}
                    {otpBusy
                      ? t("auth.login.platformOtp.verifying")
                      : t("auth.login.platformOtp.verify")}
                  </Button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground underline hover:text-foreground"
                      onClick={() => {
                        setOtpStep(null);
                        setOtpCode("");
                      }}
                    >
                      {t("auth.login.platformOtp.back")}
                    </button>
                    <button
                      type="button"
                      className="text-sm text-muted-foreground underline hover:text-foreground"
                      disabled={otpResendBusy}
                      onClick={handleOtpResend}
                    >
                      {otpResendBusy
                        ? t("auth.login.platformOtp.resending")
                        : t("auth.login.platformOtp.resend")}
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main admin login form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="flex w-full max-w-md flex-col gap-6">
        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-2 text-center">
                <img
                  src="/brand/gestionalo-logo.svg"
                  alt="Gestionalo"
                  className="mb-2 h-10 w-auto"
                />
                <h1 className="text-xl font-bold">
                  {t("auth.adminLogin.title")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.adminLogin.subtitle")}
                </p>
              </div>
              <form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="grid gap-2">
                  <Label htmlFor="admin-email">
                    {t("auth.adminLogin.email")}
                  </Label>
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="admin@gestionalo.uy"
                    {...register("email", { required: true })}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">
                      {t("auth.login.error", "El correo es obligatorio.")}
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="admin-password">
                    {t("auth.adminLogin.password")}
                  </Label>
                  <PasswordInput
                    id="admin-password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    {...register("password", { required: true })}
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive">
                      {t("auth.login.error", "La contraseña es obligatoria.")}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  )}
                  {isSubmitting
                    ? t("auth.adminLogin.signingIn")
                    : t("auth.adminLogin.signIn")}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
